import React, { useCallback, useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebaseConfig";
import { useAuth } from "../../auth/AuthProvider";
import { McPageHeader, McPageFooter } from "../components/McPageLayout";
import { getMcActiveOrgId } from "../utils/mcCloudSync";
import { isAdminProfile, useCurrentMcProfile } from "../utils/profileAccess";
import { recordMcTrafficFirestoreRead } from "../utils/mcTrafficProbe";
import MonthlyBillPreview from "../../billing/MonthlyBillPreview";
import BillingAccount from "../../billing/BillingAccount";
import { currentBillingMonth, getMonthlyBillPreview, previewErrorMessage } from "../../billing/billingPreviewService";
import "./mcImportEngine.css";
import "./mcBilling.css";
import "./mcBillingMonthly.css";

const money = (cents) => cents == null ? "Needs review" : new Intl.NumberFormat("en-ZA", {
  style: "currency", currency: "ZAR", maximumFractionDigits: 2,
}).format(cents / 100);
const dateText = (value) => value ? new Intl.DateTimeFormat("en-ZA", {
  timeZone: "Africa/Johannesburg", dateStyle: "medium",
}).format(new Date(value)) : "—";
const monthText = (month) => new Intl.DateTimeFormat("en-ZA", {
  timeZone: "Africa/Johannesburg", month: "long", year: "numeric",
}).format(new Date(`${month}-01T12:00:00+02:00`));
const statusLabel = (status) => status ? String(status).replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()) : "Unavailable";

export function MorpheusCountBillingContent({ embedded = false }) {
  const currentProfile = useCurrentMcProfile();
  const { user } = useAuth();
  const allowed = isAdminProfile(currentProfile);
  const orgId = String(getMcActiveOrgId() || "").trim();
  const uid = user?.uid || "";
  const month = currentBillingMonth();
  const scope = `${orgId}|${uid}|${month}`;
  const [usage, setUsage] = useState(null);
  const [orgState, setOrgState] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const current = allowed && usage?.scope === scope ? usage : null;
  const preview = current?.preview || null;
  const meta = allowed && orgState?.scope === scope ? orgState.meta : null;
  const orgName = preview?.orgName || meta?.name || currentProfile?.orgName || "Your company";
  const status = String(meta?.billing?.mcStatus || meta?.billing?.morpheusStatus || meta?.mcStatus || meta?.billing?.status || "").toLowerCase();

  useEffect(() => {
    let active = true;
    if (!allowed || !orgId || !uid) return undefined;
    const stillCurrent = () => active && String(getMcActiveOrgId() || "").trim() === orgId && auth.currentUser?.uid === uid;
    setUsage({ scope, preview: null, loading: true, error: "" });
    // One cached org/month preview replaces local lifetime totals and per-site Neo probes.
    getMonthlyBillPreview(orgId, month, refresh > 0).then((data) => {
      if (stillCurrent()) setUsage({ scope, preview: data, loading: false, error: "" });
    }).catch((error) => {
      if (stillCurrent()) setUsage({ scope, preview: null, loading: false, error: previewErrorMessage(error) });
    });
    // Account status is separate from accumulating usage. Only one org read is needed.
    getDoc(doc(db, "orgs", orgId)).then((snapshot) => {
      const data = snapshot.exists() ? snapshot.data() : null;
      recordMcTrafficFirestoreRead("billing:org", JSON.stringify(data || {}), "direct", { documentData: data || {} });
      if (stillCurrent()) setOrgState({ scope, meta: data });
    }).catch(() => {
      if (stillCurrent()) setOrgState({ scope, meta: null });
    });
    return () => { active = false; };
  }, [allowed, orgId, uid, month, scope, refresh]);

  const acceptPreview = useCallback((data) => {
    if (data.orgId === orgId && data.period.periodKey === month && auth.currentUser?.uid === uid && String(getMcActiveOrgId() || "").trim() === orgId) {
      setUsage({ scope, preview: data, loading: false, error: "" });
    }
  }, [orgId, uid, month, scope]);
  const acceptAccount = useCallback((summary) => {
    if (auth.currentUser?.uid !== uid || String(getMcActiveOrgId() || "").trim() !== orgId) return;
    setOrgState((previous) => {
      const previousMeta = previous?.scope === scope ? previous.meta : null;
      return { scope, meta: { ...previousMeta, billing: { ...previousMeta?.billing,
        mcStatus: summary.access.mc.status, neoStatus: summary.access.neo.status,
      } } };
    });
  }, [orgId, uid, scope]);

  if (!allowed) return <div className={embedded ? "mc-billingEmbedded" : "mc-enginePage mc-tabPage"}>
    <McPageHeader activeKey="billing" subtitle="Only admin profiles can view MC billing." embedded={embedded} />
    {!embedded && <McPageFooter />}
  </div>;

  const busy = Boolean(orgId && uid && (!current || current.loading));
  const rows = preview?.mc.rows || [];
  const bundled = preview?.pricingMode === "enterprise_bundle";
  const needsReview = preview && (preview.sourceComplete === false || preview.totalCents == null);
  const companyTotal = preview ? needsReview ? "Needs review" : money(preview.totalCents) : "—";
  const mcAmount = preview ? needsReview ? "Needs review" : bundled ? "Included" : money(preview.mc.amountCents) : "—";
  const warnings = preview ? [...new Set([...(preview.warnings || []), ...(preview.startsPending ? ["Some new count starts are waiting to be confirmed. Refresh after the connection recovers."] : [])])] : [];

  return <div className={embedded ? "mc-billingEmbedded" : "mc-enginePage mc-tabPage"} id="billing" aria-busy={busy}>
    {!embedded && <div className="mc-enginePage__grid" />}
    <div className="mc-enginePage__ambient mc-enginePage__ambient--one" />
    <div className="mc-enginePage__ambient mc-enginePage__ambient--two" />
    <McPageHeader activeKey="billing" embedded={embedded} />

    <section className="mc-enginePage__heroCards mc-billingCards mc-billingCards--five">
      <article className="mc-engineCard"><span>Status</span><strong>{statusLabel(status)}</strong><small>See issued bills for payment details</small></article>
      <article className="mc-engineCard"><span>Usage month</span><strong>{month}</strong><small>{monthText(month)}</small></article>
      <article className="mc-engineCard"><span>Configured sites</span><strong>{preview ? rows.filter((row) => row.active).length : "—"}</strong><small>{preview ? `${rows.length} sites in this month’s breakdown` : "Loading monthly usage"}</small></article>
      <article className="mc-engineCard"><span>Billable sites</span><strong>{preview ? preview.mc.billableSites : "—"}</strong><small>Started a count this month{preview?.mc.trial ? " · Trial" : ""}</small></article>
      <article className="mc-engineCard">
        <span>MC this month</span><strong>{mcAmount}</strong>
        <small>{preview ? `Neo ${bundled ? "Included" : money(preview.neo.amountCents)} · Total ${companyTotal}` : "Monthly usage estimate"}</small>
        <BillingAccount key={scope} orgId={orgId} orgName={orgName} label="View bills" className="mc-engineBtn mc-engineBtn--primary" onAccountChange={acceptAccount} />
      </article>
    </section>

    <section className="mc-enginePanel mc-billingStatusPanel">
      <div><h2>MC account</h2><p>View issued bills and payments, or review this month’s usage below.</p></div>
      <div className="mc-billingStatusRail">{["trial", "active", "past_due", "suspended"].map((state) => <span key={state} className={`mc-billingStatusPill mc-billingStatusPill--${state} ${status === state || (status === "paid" && state === "active") ? "is-active" : ""}`}>{statusLabel(state)}</span>)}</div>
    </section>

    <section className="mc-enginePanel">
      <div className="mc-engineSectionTitle"><h2>Site usage · {monthText(month)}</h2><div className="mc-billingMonthActions">
        <button type="button" className="mc-engineBtn mc-engineBtn--ghost" disabled={busy || !orgId || !uid} onClick={() => setRefresh((value) => value + 1)}>Refresh usage</button>
        <MonthlyBillPreview key={scope} orgId={orgId} orgName={orgName} currentPreview={preview} onPreviewChange={acceptPreview} disabled={busy || !uid} />
      </div></div>
      <p className="mc-billingMonthHint">One site fee when a real count starts this month. Completing or restoring an older count adds no fee.</p>
      {busy && <p className="mc-billingEmpty" role="status">Loading this month’s usage…</p>}
      {current?.error && <p className="mc-billingMonthNotice" role="alert">{current.error}</p>}
      {!orgId && <p className="mc-billingMonthNotice" role="alert">Select your company to view billing.</p>}
      {orgId && !uid && <p className="mc-billingMonthNotice" role="alert">Sign in again to view billing.</p>}
      {preview && <>
        <div className="mc-billingMonthScroll" role="region" aria-label="Monthly MC site usage" tabIndex={0}><table className="mc-billingMonthTable">
          <thead><tr><th scope="col">Site</th><th scope="col">Counts started</th><th scope="col">First count</th><th scope="col">Monthly fee</th></tr></thead>
          <tbody>{rows.map((row) => <tr key={row.siteId}>
            <th scope="row">{row.siteName}<small className="mc-billingSiteMeta">{row.active ? "Active" : "Inactive"}{row.countStarts ? row.billable ? " · Used this month" : " · Exempt" : " · No new count"}{row.withNeo && row.countStarts ? " · With Neo" : ""}{row.trial ? " · Trial" : ""}</small></th>
            <td>{row.countStarts}</td><td>{dateText(row.firstStartedAt)}</td><td>{needsReview ? "Needs review" : bundled ? "Included" : money(row.amountCents)}</td>
          </tr>)}</tbody>
          {rows.length > 0 && <tfoot><tr><th scope="row">MC total</th><td>{preview.mc.countStarts}</td><td /><td>{mcAmount}</td></tr></tfoot>}
        </table>{!rows.length && <p className="mc-billingEmpty">No MC sites are configured for this month.</p>}</div>
        {warnings.length > 0 && <details className="mc-billingMonthNotice"><summary>Checks before issuing a bill</summary><ul>{warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul></details>}
      </>}
    </section>
    {!embedded && <McPageFooter />}
  </div>;
}

export default function MorpheusCountBilling() {
  return <MorpheusCountBillingContent />;
}
